/**
 * DeactivateModuleModal.tsx — F7a T7.4.
 *
 * Modal confirm wywołujący POST /api/admin/tenants/:id/modules/:code/deactivate.
 * Nie używany dla modułów core — button jest disabled wyżej w tabeli.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

export interface DeactivateModuleModalProps {
  open: boolean;
  tenantId: string;
  moduleCode: string;
  moduleName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function DeactivateModuleModal({
  open,
  tenantId,
  moduleCode,
  moduleName,
  onClose,
  onSuccess,
}: DeactivateModuleModalProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setIsSubmitting(false);
      setApiError(null);
    }
    prevOpenRef.current = open;
  }, [open]);

  async function handleConfirm() {
    setIsSubmitting(true);
    setApiError(null);
    try {
      await api.post(`/admin/tenants/${tenantId}/modules/${moduleCode}/deactivate`);
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error: string }>;
      const code = axErr.response?.data?.error;
      let msg: string;
      if (code === 'CANNOT_DEACTIVATE_CORE') {
        msg = t('admin.tenants.detail.modules.deactivateModal.errorCore');
      } else if (code === 'ALREADY_CANCELLED') {
        msg = t('admin.tenants.detail.modules.deactivateModal.errorAlreadyCancelled');
      } else if (code === 'SUBSCRIPTION_NOT_FOUND') {
        msg = t('admin.tenants.detail.modules.deactivateModal.errorNotFound');
      } else {
        msg = t('admin.tenants.detail.modules.deactivateModal.errorGeneric');
      }
      setApiError(msg);
      setIsSubmitting(false);
    }
  }

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
        {t('admin.tenants.detail.modules.deactivateModal.cancel')}
      </Button>
      <Button
        variant="danger"
        onClick={() => {
          void handleConfirm();
        }}
        loading={isSubmitting}
        disabled={isSubmitting}
      >
        {isSubmitting
          ? t('admin.tenants.detail.modules.deactivateModal.confirming')
          : t('admin.tenants.detail.modules.deactivateModal.confirm')}
      </Button>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin.tenants.detail.modules.deactivateModal.title')}
      description={t('admin.tenants.detail.modules.deactivateModal.description', {
        module: moduleName,
      })}
      size="sm"
      footer={footer}
      closeOnBackdrop={!isSubmitting}
      closeOnEsc={!isSubmitting}
    >
      <p className="text-sm text-text-secondary">
        {t('admin.tenants.detail.modules.deactivateModal.warning')}
      </p>
      {apiError && (
        <div
          role="alert"
          className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300"
        >
          {apiError}
        </div>
      )}
    </Modal>
  );
}
